import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, ChevronLeft, ChevronRight, CreditCard, Image as ImageIcon, KeyRound, LogOut, Mail, Phone, Shield, User } from 'lucide-react';
import ImageUploadModal from '../../user/account/ImageUploadModal';

const API = process.env.REACT_APP_API_URL;
const PERSONAL_FIELDS = ['username', 'email', 'phone_number'];
const BANK_FIELDS = ['name_on_bank_account', 'bank_name', 'bank_account', 'interbancario_bank_account'];

const sections = [
  { id: 'personal', label: 'Datos personales', description: 'Nombre, correo, teléfono e imágenes de perfil', icon: User },
  { id: 'bank', label: 'Datos bancarios', description: 'Información de cuenta para el trabajador', icon: CreditCard },
  { id: 'security', label: 'Seguridad', description: 'Cambiar tu contraseña', icon: KeyRound },
];

function accountDraft(user) {
  return Object.fromEntries([...PERSONAL_FIELDS, ...BANK_FIELDS].map(field => [field, user?.[field] || '']));
}

function MyAccountAdmin({ userId, onUpdateUser, onLogout, setActiveComponent }) {
  const [user, setUser] = useState(null);
  const [draft, setDraft] = useState(accountDraft(null));
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [imageType, setImageType] = useState(null);
  const [password, setPassword] = useState({ current: '', next: '', confirm: '' });

  const fetchUser = useCallback(async () => {
    if (!userId) {
      setError('No se encontró el usuario de esta sesión.');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/user_edit/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setUser(data.user);
      setDraft(accountDraft(data.user));
      setError('');
    } catch {
      setError('No se pudo cargar tu cuenta. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  function chooseSection(next) {
    setSection(next);
    setDraft(accountDraft(user));
    setPassword({ current: '', next: '', confirm: '' });
    setError('');
    setNotice('');
  }

  async function saveFields(fields) {
    const payload = Object.fromEntries(fields.map(field => {
      const value = draft[field].trim();
      return [field, fields === BANK_FIELDS ? value || null : value];
    }));
    if (fields === PERSONAL_FIELDS && (!payload.username || !payload.email || !payload.phone_number)) {
      setError('Nombre, correo y teléfono son obligatorios.');
      return;
    }
    if (fields === PERSONAL_FIELDS && payload.phone_number.length > 15) {
      setError('El teléfono no puede superar 15 caracteres.');
      return;
    }
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const { data } = await axios.put(`${API}/api/user_edit/${userId}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setUser(data.user);
      setDraft(accountDraft(data.user));
      if (fields === PERSONAL_FIELDS && onUpdateUser) {
        // The profile is saved even if refreshing the shell summary fails.
        try { await onUpdateUser(); } catch { /* Reloading the account will refresh it. */ }
      }
      setNotice('Cambios guardados correctamente.');
    } catch (requestError) {
      setError(requestError.response?.status === 409 ? 'Este correo o teléfono ya está en uso.' : 'No se pudieron guardar los cambios. Revisa los datos e intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  }

  async function changePassword(event) {
    event.preventDefault();
    if (password.next.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password.next !== password.confirm) {
      setError('Las contraseñas nuevas no coinciden.');
      return;
    }
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await axios.put(`${API}/api/admin/my-account/password`, {
        current_password: password.current,
        new_password: password.next,
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setPassword({ current: '', next: '', confirm: '' });
      setNotice('Contraseña actualizada correctamente.');
    } catch (requestError) {
      setError(requestError.response?.status === 401 ? 'La contraseña actual no es correcta.' : 'No se pudo actualizar la contraseña.');
    } finally {
      setSaving(false);
    }
  }

  async function updateImage(imageData) {
    const field = imageType === 'profile' ? 'profile_image' : 'background_image';
    setSaving(true);
    setError('');
    try {
      const { data } = await axios.put(`${API}/api/user_edit/${userId}`, { [field]: imageData }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setUser(data.user);
      setImageType(null);
      setNotice('Imagen guardada correctamente.');
    } catch {
      setError('No se pudo guardar la imagen.');
    } finally {
      setSaving(false);
    }
  }

  async function deleteImage() {
    setSaving(true);
    setError('');
    try {
      await axios.delete(`${API}/api/user_edit/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        data: { image_type: imageType },
      });
      setUser(current => ({ ...current, [imageType === 'profile' ? 'profile_image' : 'background_image']: null }));
      setImageType(null);
      setNotice('Imagen eliminada.');
    } catch {
      setError('No se pudo eliminar la imagen.');
    } finally {
      setSaving(false);
    }
  }

  const back = () => section ? chooseSection(null) : setActiveComponent('OptionsAdmin');

  return (
    <div className="flex h-screen flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <header className="flex-shrink-0 border-b border-slate-200 bg-white px-4 py-4 shadow-sm">
        <div className="mx-auto max-w-4xl">
          <button type="button" onClick={back} className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E]"><ChevronLeft className="h-5 w-5" />Volver</button>
        </div>
      </header>
      <main className="flex-1 overflow-y-auto px-4 py-6">
        <div className="mx-auto max-w-4xl space-y-6">
          {loading ? <p role="status" className="text-slate-600">Cargando cuenta...</p> : !user ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
              <p role="alert" className="text-red-700">{error}</p>
              <button type="button" onClick={fetchUser} className="mt-3 font-semibold text-[#B1336E]">Reintentar</button>
            </div>
          ) : <>
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="relative h-32 bg-gradient-to-r from-[#B1336E] to-[#E6B43C] sm:h-40">
                {user.background_image && <img src={user.background_image} alt="Fondo de perfil" className="h-full w-full object-cover" />}
                <button type="button" onClick={() => setImageType('background')} aria-label="Editar imagen de fondo" className="absolute right-3 top-3 rounded-lg bg-white p-2 text-[#B1336E]"><ImageIcon className="h-5 w-5" /></button>
              </div>
              <div className="px-6 pb-6">
                <div className="relative -mt-10 h-20 w-20 rounded-full border-4 border-white bg-gradient-to-br from-[#B1336E] to-[#E6B43C] shadow-lg">
                  {user.profile_image ? <img src={user.profile_image} alt="Foto de perfil" className="h-full w-full rounded-full object-cover" /> : <User className="m-auto h-full w-9 text-white" />}
                  <button type="button" onClick={() => setImageType('profile')} aria-label="Editar foto de perfil" className="absolute -bottom-1 -right-1 rounded-full bg-white p-2 text-[#B1336E] shadow"><ImageIcon className="h-4 w-4" /></button>
                </div>
                <h1 className="mt-4 text-2xl font-extrabold text-[#B1336E]">Mi cuenta</h1>
                <p className="mt-1 text-slate-800">{user.username}</p>
                <p className="text-sm text-slate-500">{user.role === 'master' ? 'Master' : 'Administrador'} · Usuario #{user.id}</p>
              </div>
            </div>

            {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">{error}</p>}
            {notice && <p role="status" className="rounded-xl border border-green-200 bg-green-50 p-4 text-green-800">{notice}</p>}

            {!section ? <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-100">
              {sections.map(({ id, label, description, icon: Icon }) => <button key={id} type="button" onClick={() => chooseSection(id)} className="flex w-full items-center gap-4 p-5 text-left hover:bg-slate-50 focus-visible:outline-[#B1336E]">
                <span className="rounded-xl bg-[#B1336E]/10 p-3 text-[#B1336E]"><Icon className="h-6 w-6" /></span>
                <span className="min-w-0 flex-1"><span className="block font-semibold text-slate-900">{label}</span><span className="block text-sm text-slate-600">{description}</span></span>
                <ChevronRight className="h-5 w-5 text-slate-400" />
              </button>)}
            </div> : section === 'security' ? <form onSubmit={changePassword} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-bold text-slate-900">Seguridad</h2>
              <p className="text-sm text-slate-600">Confirma tu contraseña actual para cambiarla.</p>
              <Field label="Contraseña actual" type="password" value={password.current} onChange={value => setPassword(current => ({ ...current, current: value }))} autoComplete="current-password" />
              <Field label="Nueva contraseña" type="password" value={password.next} onChange={value => setPassword(current => ({ ...current, next: value }))} autoComplete="new-password" />
              <Field label="Confirmar nueva contraseña" type="password" value={password.confirm} onChange={value => setPassword(current => ({ ...current, confirm: value }))} autoComplete="new-password" />
              <SaveButton saving={saving} />
            </form> : <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-xl font-bold text-slate-900">{section === 'personal' ? 'Datos personales' : 'Datos bancarios'}</h2>
              {section === 'personal' ? <>
                <Field label="Nombre de usuario" value={draft.username} onChange={value => setDraft(current => ({ ...current, username: value }))} maxLength={120} icon={User} required />
                <Field label="Correo electrónico" type="email" value={draft.email} onChange={value => setDraft(current => ({ ...current, email: value }))} maxLength={120} icon={Mail} required />
                <Field label="Teléfono" type="tel" value={draft.phone_number} onChange={value => setDraft(current => ({ ...current, phone_number: value }))} maxLength={15} icon={Phone} required />
                <p className="text-sm text-slate-600">Cambiar el correo requerirá verificar la nueva dirección.</p>
                <button type="button" disabled={saving} onClick={() => saveFields(PERSONAL_FIELDS)} className="rounded-xl bg-[#B1336E] px-5 py-3 font-semibold text-white disabled:opacity-50">{saving ? 'Guardando...' : 'Guardar cambios'}</button>
              </> : <>
                <Field label="Titular de la cuenta" value={draft.name_on_bank_account} onChange={value => setDraft(current => ({ ...current, name_on_bank_account: value }))} maxLength={120} icon={User} />
                <Field label="Banco" value={draft.bank_name} onChange={value => setDraft(current => ({ ...current, bank_name: value }))} maxLength={120} icon={Building2} />
                <Field label="Número de cuenta" value={draft.bank_account} onChange={value => setDraft(current => ({ ...current, bank_account: value }))} maxLength={120} icon={CreditCard} />
                <Field label="Cuenta interbancaria (CCI)" value={draft.interbancario_bank_account} onChange={value => setDraft(current => ({ ...current, interbancario_bank_account: value }))} maxLength={120} icon={Shield} />
                <button type="button" disabled={saving} onClick={() => saveFields(BANK_FIELDS)} className="rounded-xl bg-[#B1336E] px-5 py-3 font-semibold text-white disabled:opacity-50">{saving ? 'Guardando...' : 'Guardar cambios'}</button>
              </>}
            </div>}
            <ImageUploadModal key={imageType || 'closed'} isOpen={Boolean(imageType)} onClose={() => !saving && setImageType(null)} onUpload={updateImage} onDelete={deleteImage} currentImage={imageType === 'profile' ? user.profile_image : user.background_image} imageType={imageType} loading={saving} />
          </>}
        </div>
      </main>
      <footer className="flex-shrink-0 border-t border-slate-200 bg-white p-4">
        <button type="button" onClick={onLogout} className="mx-auto flex w-full max-w-4xl items-center justify-center gap-2 rounded-xl border-2 border-red-200 p-3 font-semibold text-red-600 hover:bg-red-50"><LogOut className="h-5 w-5" />Cerrar sesión</button>
      </footer>
    </div>
  );
}

function Field({ label, value, onChange, icon: Icon, type = 'text', ...props }) {
  return <label className="block text-sm font-medium text-slate-700">
    <span className="mb-1 flex items-center gap-2">{Icon && <Icon className="h-4 w-4 text-[#B1336E]" />}{label}</span>
    <input type={type} value={value} onChange={event => onChange(event.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-[#B1336E] focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20" {...props} />
  </label>;
}

function SaveButton({ saving }) {
  return <button type="submit" disabled={saving} className="rounded-xl bg-[#B1336E] px-5 py-3 font-semibold text-white disabled:opacity-50">{saving ? 'Guardando...' : 'Cambiar contraseña'}</button>;
}

export default MyAccountAdmin;
